import React, { useCallback, useEffect, useRef, useState } from "react";
import { settingsRequest } from "./settingsUtils";
import {
  actionsStyle,
  cardStyle,
  helpStyle,
  noticeStyle,
  planRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  sectionTitleStyle,
  statusStyle,
  warningStyle
} from "./settingsStyles";

// Checkout redirects the browser back before Stripe's webhook necessarily lands, and the
// webhook is what actually grants Pro. Re-read the status a few times rather than trusting
// the redirect, which a user can also open by hand.
const REFRESH_DELAYS_MS = [0, 2000, 5000, 9000];

const STATUS_LABELS = {
  active: "Active",
  trialing: "Trial",
  past_due: "Payment overdue",
  unpaid: "Unpaid",
  canceled: "Cancelled",
  incomplete: "Awaiting payment",
  incomplete_expired: "Expired"
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

const BillingSection = ({ token }) => {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const timers = useRef([]);

  const load = useCallback(async () => {
    try {
      setBilling(await settingsRequest("/billing/status", "GET", null, token));
    } catch {
      // Billing status is non-critical chrome; the rest of Settings must still render.
    }
  }, [token]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const refreshUntilSettled = useCallback(() => {
    clearTimers();
    timers.current = REFRESH_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        setBilling((current) => {
          if (current?.plan_id === "pro") return current;
          load();
          return current;
        });
      }, delay)
    );
  }, [load]);

  useEffect(() => {
    load();
    return clearTimers;
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (!checkout) return;

    if (checkout === "success") {
      setMessage("Payment received. Confirming your subscription with Stripe...");
      refreshUntilSettled();
    } else {
      setMessage("Checkout was cancelled. Your plan is unchanged.");
    }
    window.history.replaceState({}, "", "/settings");
  }, [refreshUntilSettled]);

  const startCheckout = async () => {
    setLoading(true);
    setMessage("Opening Stripe Checkout...");
    try {
      const data = await settingsRequest("/billing/checkout-session", "POST", null, token);
      window.location.href = data.url;
    } catch (error) {
      setMessage(error.message || "Checkout could not be started.");
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    setMessage("Opening the Stripe billing portal...");
    try {
      const data = await settingsRequest("/billing/portal-session", "POST", null, token);
      window.location.href = data.url;
    } catch (error) {
      setMessage(error.message || "The billing portal could not be opened.");
      setLoading(false);
    }
  };

  const isPro = billing?.plan_id === "pro";
  // The period end sticks around after a subscription ends, so only show it while Pro is
  // actually in force. Otherwise a cancelled workspace reads "Renews on <past date>".
  const renewalDate = isPro ? formatDate(billing?.current_period_end) : null;
  const statusLabel = billing?.status ? STATUS_LABELS[billing.status] || billing.status : "No subscription";

  return (
    <section style={cardStyle}>
      <h2 style={sectionTitleStyle}>Billing</h2>
      <p style={helpStyle}>
        The subscription covers this whole workspace. Payments and invoices are handled by Stripe.
      </p>

      {message && <p style={noticeStyle}>{message}</p>}

      {billing?.payment_issue && (
        <div style={warningStyle}>
          <strong>Payment failed</strong>
          <span>Update your card in the billing portal to keep Pro.</span>
        </div>
      )}

      {billing?.cancel_at_period_end && renewalDate && (
        <div style={warningStyle}>
          <strong>Cancelling</strong>
          <span>Pro ends on {renewalDate}.</span>
        </div>
      )}

      <div style={statusStyle}>
        <strong>Current plan</strong>
        <span>{billing ? `${billing.plan_name} · $${billing.monthly_price_usd}/month` : "Loading..."}</span>
      </div>

      {billing && (
        <>
          <div style={planRowStyle}>
            <span>Subscription status</span>
            <span>{statusLabel}</span>
          </div>
          {renewalDate && (
            <div style={planRowStyle}>
              <span>{billing.cancel_at_period_end ? "Access ends" : "Renews on"}</span>
              <span>{renewalDate}</span>
            </div>
          )}
        </>
      )}

      {billing && !billing.stripe_configured && (
        <p style={helpStyle}>Billing is not configured on this server.</p>
      )}

      <div style={{ ...actionsStyle, marginTop: "18px" }}>
        {!isPro && (
          <button
            style={primaryButtonStyle}
            onClick={startCheckout}
            disabled={loading || !billing?.can_manage || !billing?.stripe_configured}
          >
            {loading ? "Working..." : "Upgrade to Pro"}
          </button>
        )}
        {billing?.has_billing_account && (
          <button
            style={isPro ? primaryButtonStyle : secondaryButtonStyle}
            onClick={openPortal}
            disabled={loading || !billing?.can_manage || !billing?.stripe_configured}
          >
            Manage billing
          </button>
        )}
      </div>
    </section>
  );
};

export default BillingSection;
